'use client';

import { StyledHeading } from '@/components/styled-heading';
import { Button } from '@/components/ui/button';
import { HeroModuleShared } from '@veranda/shared/components/modules';
import type { HomePageProps } from '@veranda/shared/components/pages/home/client';
import { Video } from '@veranda/shared/components/video';
import { cn } from '@veranda/shared/lib';
import { SanityImage } from '@veranda/shared/sanity/utils/components';

type Props = NonNullable<HomePageProps['hero']>;

export function HeroModule({ data }: { data: Props }) {
  if (!data || !data.items) return null;

  const allTitlesAreHidden = data.items?.every((item) => item.hideTitle);
  const firstTitle = data.items[0]?.title;
  const hasNoAssets = data.items?.every((item) => !item.asset);

  if (allTitlesAreHidden && firstTitle && hasNoAssets) {
    return <h1 className="sr-only">{firstTitle}</h1>;
  }

  return (
    <HeroModuleShared
      settings={
        data.hasFixedAspectRatio
          ? {
              type: 'aspectRatio',
              mobileAspectRatio: data.mobileAspectRatio ?? '1:1',
              desktopAspectRatio: data.desktopAspectRatio ?? '16:9',
            }
          : {
              type: 'moduleWrapper',
              moduleWrapper: data.moduleSettings ?? {
                topPadding: 'medium',
                bottomPadding: 'medium',
              },
            }
      }
    >
      {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation> */}
      {data.items?.map((item, index) => {
        const hasBackgroundAsset = item.asset !== null;
        const hasTitle = item.title && !item.hideTitle;
        const assetWidth = data.hasFixedAspectRatio ? null : item.assetWidth;

        const assetWidthClassName = cn(
          assetWidth === 'medium' && 'max-w-full lg:max-w-5xl',
          assetWidth === 'small' && 'max-w-full lg:max-w-3xl'
        );
        const textAlignmentClassName = cn(
          item.textAlignment === 'center' && 'items-center text-center'
        );

        const textColorStyleProp = item.textColor
          ? { style: { color: item.textColor } }
          : undefined;

        return (
          <HeroModuleShared.Wrapper
            href={item.link?.slug ?? '#'}
            target={item.link?.openInNewTab ? '_blank' : '_self'}
            key={item.link?.slug}
          >
            <HeroModuleShared.Content
              hasBackgroundAsset={hasBackgroundAsset}
              verticalAlignmentMobile={item.verticalAlignmentMobile}
              verticalAlignmentDesktop={item.verticalAlignmentDesktop}
            >
              {item.title && (
                <StyledHeading
                  config={item.titleConfig}
                  className={cn(
                    item.hideTitle && 'sr-only',
                    textAlignmentClassName
                  )}
                >
                  <h1 {...textColorStyleProp}>{item.title}</h1>
                </StyledHeading>
              )}
              {item.subtitle && (
                <StyledHeading
                  config={item.subtitleConfig}
                  className={textAlignmentClassName}
                >
                  <p
                    {...textColorStyleProp}
                    className="max-w-screen-md text-pretty"
                  >
                    {item.subtitle?.split('\n').map((line, lineIndex) => (
                      <span key={lineIndex}>
                        {line}
                        {lineIndex <
                          (item.subtitle?.split('\n').length ?? 0) - 1 && (
                          <br />
                        )}
                      </span>
                    ))}
                  </p>
                </StyledHeading>
              )}
              {item.link?.show && (
                <Button
                  variant={item.buttonVariant ?? 'primary'}
                  asChild
                  className={textAlignmentClassName}
                >
                  <span>{item.link.text}</span>
                </Button>
              )}
            </HeroModuleShared.Content>
            {hasBackgroundAsset && hasTitle && <HeroModuleShared.Overlay />}
            {/* Single image asset */}
            {item.asset &&
              'type' in item.asset &&
              item.asset.type === 'image' &&
              item.asset.sameAssetForMobileAndDesktop === true &&
              'asset' in item.asset &&
              'crop' in item.asset &&
              'hotspot' in item.asset && (
                <SanityImage
                  image={{
                    asset: item.asset.asset,
                    crop: item.asset.crop,
                    hotspot: item.asset.hotspot,
                  }}
                  fill={data.hasFixedAspectRatio}
                  sizes="(max-width: 767px) 100vw, (max-width: 1023px) 100vw, 1600px"
                  className={cn(
                    data.hasFixedAspectRatio && 'object-cover',
                    !data.hasFixedAspectRatio && 'mx-auto',
                    assetWidthClassName
                  )}
                  priority={index === 0}
                />
              )}
            {/* Separate mobile/desktop images */}
            {item.asset &&
              'imageMobile' in item.asset &&
              'imageDesktop' in item.asset && (
                <>
                  {item.asset.imageMobile && (
                    <SanityImage
                      image={item.asset.imageMobile}
                      fill={data.hasFixedAspectRatio}
                      sizes="(max-width: 767px) 100vw, (max-width: 1023px) 100vw, 1600px"
                      className={cn(
                        data.hasFixedAspectRatio && 'object-cover',
                        !data.hasFixedAspectRatio && 'mx-auto',
                        assetWidthClassName,
                        'md:hidden'
                      )}
                      priority={index === 0}
                    />
                  )}
                  {item.asset.imageDesktop && (
                    <SanityImage
                      image={item.asset.imageDesktop}
                      fill={data.hasFixedAspectRatio}
                      sizes="(max-width: 767px) 100vw, (max-width: 1023px) 100vw, 1600px"
                      className={cn(
                        data.hasFixedAspectRatio && 'object-cover',
                        !data.hasFixedAspectRatio && 'mx-auto',
                        assetWidthClassName,
                        'hidden md:block'
                      )}
                      priority={index === 0}
                    />
                  )}
                </>
              )}
            {/* Single video asset */}
            {item.asset &&
              'type' in item.asset &&
              item.asset.type === 'video' &&
              item.asset.sameAssetForMobileAndDesktop === true &&
              'playbackId' in item.asset &&
              item.asset.playbackId && (
                <Video
                  playbackId={item.asset.playbackId as string}
                  loading="eager"
                  resolution="HD"
                  controlled={false}
                  className={cn(
                    data.hasFixedAspectRatio && 'object-cover',
                    !data.hasFixedAspectRatio && 'mx-auto',
                    assetWidthClassName
                  )}
                />
              )}
            {/* Separate mobile/desktop videos */}
            {item.asset &&
              'videoMobile' in item.asset &&
              'videoDesktop' in item.asset && (
                <>
                  {item.asset.videoMobile &&
                    'playbackId' in item.asset.videoMobile &&
                    item.asset.videoMobile.playbackId && (
                      <Video
                        playbackId={item.asset.videoMobile.playbackId}
                        loading="eager"
                        resolution="HD"
                        controlled={true}
                        className={cn(
                          data.hasFixedAspectRatio && 'object-cover',
                          !data.hasFixedAspectRatio && 'mx-auto',
                          assetWidthClassName,
                          'md:hidden'
                        )}
                      />
                    )}
                  {item.asset.videoDesktop &&
                    'playbackId' in item.asset.videoDesktop &&
                    item.asset.videoDesktop.playbackId && (
                      <Video
                        playbackId={item.asset.videoDesktop.playbackId}
                        loading="eager"
                        resolution="HD"
                        controlled={false}
                        className={cn(
                          data.hasFixedAspectRatio && 'object-cover',
                          !data.hasFixedAspectRatio && 'mx-auto',
                          assetWidthClassName,
                          'hidden md:block'
                        )}
                      />
                    )}
                </>
              )}
          </HeroModuleShared.Wrapper>
        );
      })}
    </HeroModuleShared>
  );
}
